import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Calendar, Lock, AlertTriangle, CheckCircle, ArrowRight, Loader2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

export default function FinancialYearClose() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/financial-year/status');
      setStatus(res.data);
    } catch { toast.error('Failed to load FY status'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleClose = async () => {
    setClosing(true);
    try {
      const s = status;
      const nextStart = getNextFYStart(s.financial_year_end);
      const nextEnd = getNextFYEnd(nextStart);
      const res = await api.post('/financial-year/close', {
        financial_year_start: s.financial_year_start,
        financial_year_end: s.financial_year_end,
        next_year_start: nextStart,
        next_year_end: nextEnd,
      });
      toast.success(res.data.message);
      setShowConfirm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to close FY'); }
    setClosing(false);
  };

  const getNextFYStart = (endDate) => {
    const d = new Date(endDate);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const getNextFYEnd = (startDate) => {
    const d = new Date(startDate);
    d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="fy-close-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Close Financial Year</h1>
        <p className="text-sm text-slate-500">Lock vouchers & carry forward balances to next FY</p>
      </div>

      {/* Current FY Status */}
      <div className="bg-white border rounded-lg p-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><Calendar size={16} /> Current Financial Year</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Period</p>
            <p className="text-sm font-bold text-slate-800">{status?.financial_year_start} to {status?.financial_year_end}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Total Vouchers</p>
            <p className="text-2xl font-bold text-slate-800">{status?.total_vouchers || 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Locked</p>
            <p className="text-2xl font-bold text-emerald-600">{status?.locked_vouchers || 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Unlocked</p>
            <p className="text-2xl font-bold text-amber-600">{status?.unlocked_vouchers || 0}</p>
          </div>
        </div>

        {/* Trial Balance Check */}
        <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 ${status?.is_balanced ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
          {status?.is_balanced ? (
            <><CheckCircle size={18} className="text-emerald-600" /><span className="text-sm text-emerald-700 font-medium">Trial Balance is balanced (Dr: {status?.total_debit} = Cr: {status?.total_credit})</span></>
          ) : (
            <><AlertTriangle size={18} className="text-red-600" /><span className="text-sm text-red-700 font-medium">Trial Balance is NOT balanced (Dr: {status?.total_debit} != Cr: {status?.total_credit}). Resolve before closing.</span></>
          )}
        </div>

        {status?.is_closed ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={16} className="text-blue-600" />
              <p className="text-sm font-semibold text-blue-800">This Financial Year is Already Closed</p>
            </div>
            <p className="text-xs text-blue-600">Closed on: {(status.closure?.closed_at || '').slice(0, 10)}</p>
            <p className="text-xs text-blue-600">Net Profit Carried Forward: {status.closure?.net_profit}</p>
            <p className="text-xs text-blue-600">Opening Balances Created: {status.closure?.opening_balances_count}</p>
          </div>
        ) : (
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-2">What happens when you close?</h3>
            <ul className="text-xs text-slate-500 space-y-1.5 mb-4">
              <li className="flex items-center gap-2"><Lock size={12} className="text-slate-400" />All vouchers for this year will be <strong>locked</strong> (no edits/deletes)</li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-slate-400" />Closing balances of balance sheet accounts will be <strong>carried forward</strong></li>
              <li className="flex items-center gap-2"><ArrowRight size={12} className="text-slate-400" />Net profit/loss will be transferred to <strong>Retained Earnings</strong></li>
              <li className="flex items-center gap-2"><Calendar size={12} className="text-slate-400" />Company financial year will update to <strong>next period</strong></li>
            </ul>
            <Button onClick={() => setShowConfirm(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fy-close-btn">
              <Lock size={14} className="mr-2" /> Close Financial Year
            </Button>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={18} /> Confirm Financial Year Closure</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">You are about to close the financial year:</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-bold text-red-800">{status?.financial_year_start} to {status?.financial_year_end}</p>
              <p className="text-xs text-red-600 mt-1">This action cannot be undone. All {status?.total_vouchers} vouchers will be permanently locked.</p>
            </div>
            <p className="text-xs text-slate-500">Opening balances will be created for the next financial year automatically.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button onClick={handleClose} disabled={closing} className="bg-red-600 hover:bg-red-700 text-white" data-testid="fy-confirm-close">
              {closing ? <><Loader2 size={14} className="animate-spin mr-1" /> Processing...</> : 'Confirm & Close Year'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
